import { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import Login from "@/pages/auth/Login";
import PhoneLogin from "@/pages/auth/PhoneLogin";
import Register from "@/pages/auth/Register";
import Dashboard from "@/pages/deals/Dashboard";
import RoleOverview from "@/pages/overview/RoleOverview";
import ExternalDeals from "@/pages/overview/ExternalDeals";
import DealDetail from "@/pages/deals/DealDetail";
import ArchivedDeals from "@/pages/deals/ArchivedDeals";
import PublicMicrosite from "@/pages/public/PublicMicrosite";
import ShortLink from "@/pages/public/ShortLink";
import InviteOutbox from "@/pages/organization/InviteOutbox";
import AcceptInvite from "@/pages/auth/AcceptInvite";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Pricing from "@/pages/public/Pricing";
import ClientDealView from "@/pages/deals/ClientDealView";
import CollabDealView from "@/pages/deals/CollabDealView";
import PrintableDealSummary from "@/pages/deals/PrintableDealSummary";
import Billing from "@/pages/organization/Billing";
import AuditLog from "@/pages/organization/AuditLog";
import HelpCenter from "@/pages/public/HelpCenter";
import Settings from "@/pages/account/Settings";
import NotificationsPage from "@/pages/account/Notifications";
import Reports from "@/pages/organization/Reports";
import ChatsPage from "@/pages/deals/Chats";
import { PUBLIC_ROUTES, PROTECTED_ROUTES, SHARED_PROTECTED_ROUTES, EXTERNAL_ROUTES } from "@/routes/url";

export type RouteItem = { path: string; component: ReactElement };

export const ALL_PUBLIC_ROUTES: RouteItem[] = [
  { path: PUBLIC_ROUTES.LANDING, component: <Navigate to={PUBLIC_ROUTES.LOGIN} replace /> },
  { path: PUBLIC_ROUTES.LOGIN, component: <Login /> },
  { path: PUBLIC_ROUTES.LOGIN_PHONE, component: <PhoneLogin /> },
  { path: PUBLIC_ROUTES.REGISTER, component: <Register /> },
  { path: PUBLIC_ROUTES.OVERVIEW, component: <RoleOverview /> },
  { path: PUBLIC_ROUTES.ACCEPT_INVITE, component: <AcceptInvite /> },
  { path: PUBLIC_ROUTES.FORGOT_PASSWORD, component: <ForgotPassword /> },
  { path: PUBLIC_ROUTES.RESET_PASSWORD, component: <ResetPassword /> },
  { path: PUBLIC_ROUTES.PRICING, component: <Pricing /> },
  { path: PUBLIC_ROUTES.PUBLIC_MICROSITE, component: <PublicMicrosite /> },
  { path: PUBLIC_ROUTES.SHORT_LINK, component: <ShortLink /> },
];

export const DASHBOARD_ROUTES: RouteItem[] = [
  { path: PROTECTED_ROUTES.DASHBOARD, component: <Dashboard /> },
  { path: PROTECTED_ROUTES.ARCHIVED_DEALS, component: <ArchivedDeals /> },
  { path: PROTECTED_ROUTES.DEAL_DETAIL, component: <DealDetail /> },
  { path: PROTECTED_ROUTES.INVITES, component: <InviteOutbox /> },
  { path: PROTECTED_ROUTES.BILLING, component: <Billing /> },
  { path: PROTECTED_ROUTES.REPORTS, component: <Reports /> },
  // Org-only, not shared. The endpoint behind it requires an org membership
  // plus an admin role, so mounting it under the shared shell only let
  // externals reach a page that could never load.
  { path: SHARED_PROTECTED_ROUTES.AUDIT, component: <AuditLog /> },
  { path: PROTECTED_ROUTES.HELP, component: <HelpCenter /> },
];

export const SHARED_DASHBOARD_ROUTES: RouteItem[] = [
  { path: SHARED_PROTECTED_ROUTES.NOTIFICATIONS, component: <NotificationsPage /> },
  { path: SHARED_PROTECTED_ROUTES.SETTINGS, component: <Settings /> },
  // Genuinely shared, unlike Audit: `GET /me/deals` is scoped to the caller's
  // own participation rows, so it answers correctly for an internal user who
  // is somebody else's client and for an external in four rooms alike.
  { path: SHARED_PROTECTED_ROUTES.SHARED_WITH_ME, component: <ExternalDeals /> },
  { path: SHARED_PROTECTED_ROUTES.CHATS, component: <ChatsPage /> },
  // The two portal views live inside the dashboard shell, not beside it.
  // Mounted bare, they rendered their own public header and no sidebar — so
  // opening a deal deleted the only navigation an external had, and the way
  // back to their other deals was the browser's back button. `embedded` is
  // what stops DetailShell adding a second header inside the shell.
  { path: EXTERNAL_ROUTES.CLIENT_DEAL, component: <ClientDealView embedded /> },
  { path: EXTERNAL_ROUTES.COLLAB_DEAL, component: <CollabDealView embedded /> },
];

export const EXTERNAL_DEAL_ROUTES: RouteItem[] = [
  // Deliberately outside every shell: this one is printed. Sidebar chrome on a
  // page whose purpose is a clean PDF would be the bug, not the fix.
  { path: PROTECTED_ROUTES.DEAL_SUMMARY_PRINT, component: <PrintableDealSummary /> },
];
